/**
 * fbclid → `_fbc` first-party cookie capture.
 *
 * Mirrors what fbevents.js does, but before the async pixel loads, so the
 * server-side CAPI always finds the cookie even when the pixel is slow or
 * blocked. Called from instrumentation-client.ts (after document load, before
 * React hydration — still ahead of every `afterInteractive` tag).
 *
 * This used to be an inline `<script>` child of the layout's <head>. It must
 * NOT go back there: React matches raw head/body children by POSITION during
 * hydration, and Meta/Instagram in-app browsers — the shells behind virtually
 * every fbclid-bearing Android visit — inject their own <script> into the
 * document before hydration. React can claim the injected script for its own
 * inline one, shifting every expectation after it (minified React #418, the
 * paid-traffic homepage crash in PostHog issue 01a03c14).
 */

const FBC_TTL_MS = 90 * 24 * 60 * 60 * 1000;

/**
 * The cookie string to write for this page view, or null when there is
 * nothing to do (no fbclid, or a `_fbc` already exists — first click wins,
 * matching what the inline script always did).
 */
export function buildFbcCookie(
  search: string,
  cookies: string,
  now: number,
): string | null {
  const fbclid = new URLSearchParams(search).get("fbclid");
  if (!fbclid || /(^|;)\s*_fbc=/.test(cookies)) return null;
  const expires = new Date(now + FBC_TTL_MS).toUTCString();
  return `_fbc=fb.1.${now}.${fbclid};expires=${expires};path=/;SameSite=Lax`;
}

/** Read the live URL/cookies and plant `_fbc`. Attribution is best-effort —
 *  never let it interfere with boot. */
export function captureFbcCookie(): void {
  try {
    const cookie = buildFbcCookie(
      window.location.search,
      document.cookie,
      Date.now(),
    );
    if (cookie) document.cookie = cookie;
  } catch {
    // Malformed URL/cookie access in an exotic shell — skip attribution.
  }
}
